// libs
import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
// dls
import { Text } from '@swiggy-private/dls-web-core/dist/components/Text';
// components
import Table from '@/widgets/Table/Table';
import CampaginsTablePlaceholder from '@/components/CampaignsTablePlaceholder';
// constants
import { PERFORMANCE_TABLE_COLUMNS } from '@/pages/instamart/home/constants';
import { DlsColorTokens, DlsFontVariants } from '@/constants/DLSTokens';
import { CAMPAIGN_TABLE_COLUMNS } from '@/pages/instamart/search/constants';
import Routes from '@/constants/Routes';
// helpers
import { getParsedMetricValue } from '@/helpers/utils';
import { format, formatNumberIntl, isEmpty, prefixWithRupee, } from '@/helpers/utils';
import { getMappedCampaignStatus } from '@/helpers/campaign';
// styles
import style from '@/widgets/Table/Table.module.scss';
import tableStyles from './Table.module.scss';
import { CampaignStatus } from '@local/typedefs/dist/protos/campaign/Campaign';
const CampaignTable = ({ campaigns, isSearchPage }) => {
    //------- Render Hooks--------//
    const navigate = useNavigate();
    const campaignsData = useMemo(() => {
        return campaigns.map((campaign) => {
            var _a, _b, _c, _d, _e;
            const endsOn = campaign.endDate
                ? dayjs(campaign.endDate).format('MMMM D, YYYY')
                : '';
            const startsOn = campaign.startDate
                ? dayjs(campaign.startDate).format('MMMM D, YYYY')
                : '';
            return {
                id: campaign.id,
                campaignName: campaign.name,
                status: campaign.status,
                statusUpdateReason: campaign.statusUpdateReason,
                endsOn,
                startsOn,
                budget: campaign.budget,
                spend: (_a = campaign.metrics) === null || _a === void 0 ? void 0 : _a.budgetBurnt,
                impressions: (_b = campaign.metrics) === null || _b === void 0 ? void 0 : _b.impressions,
                cartAdditions: (_c = campaign.metrics) === null || _c === void 0 ? void 0 : _c.addToCart,
                gmv: (_d = campaign.metrics) === null || _d === void 0 ? void 0 : _d.gmv,
                roi: (_e = campaign.metrics) === null || _e === void 0 ? void 0 : _e.roi,
                pacingEnabled: campaign.pacingEnabled ? 'Enabled' : 'Disabled',
            };
        });
    }, [campaigns]);
    const [tableData, setTableData] = useState(campaignsData);
    useEffect(() => {
        setTableData(campaignsData);
    }, [campaigns]);
    //------- Event Handlers --------//
    const handleTableSort = (sortData) => {
        const { sortBy, descending } = sortData;
        const sorted = [...tableData];
        const sortableProperties = PERFORMANCE_TABLE_COLUMNS.filter((col) => col.sortable).map((col) => col.key);
        if (sortableProperties.includes(sortBy)) {
            if (sortBy === 'endDate') {
                sorted.sort((a, b) => {
                    const aValue = dayjs(a.endsOn);
                    const bValue = dayjs(b.endsOn);
                    return descending
                        ? bValue.diff(aValue)
                        : aValue.diff(bValue);
                });
            }
            else {
                sorted.sort((a, b) => {
                    const aValue = Number(a[sortBy]);
                    const bValue = Number(b[sortBy]);
                    return descending ? bValue - aValue : aValue - bValue;
                });
            }
        }
        setTableData(sorted);
    };
    const handleCamapginRowClick = ({ id }) => {
        if (!isEmpty(id)) {
            navigate({ pathname: format(Routes.INSTAMART.CAMPAIGN_DETAILS, id) }, { preventScrollReset: true });
        }
    };
    // ------- Render Methods --------//
    const getText = (text) => {
        return (React.createElement(Text, { variant: DlsFontVariants.body_B2_Med, color: DlsColorTokens.text_Med_Emphasis }, text));
    };
    const getCampaignName = (row) => {
        return (React.createElement("div", { className: tableStyles.campaignName },
            React.createElement(Text, { variant: DlsFontVariants.body_B1_Med, color: DlsColorTokens.text_Highest_Emphasis, maxLines: 2 }, row.campaignName),
            React.createElement(Text, { variant: DlsFontVariants.subheader_S1_Bold, color: row.status === CampaignStatus.CAMPAIGN_STATUS_LIVE
                    ? DlsColorTokens.positive
                    : DlsColorTokens.text_Low_Emphasis },
                "\u25CF",
                ' ',
                getMappedCampaignStatus(row.status, row.statusUpdateReason))));
    };
    const getRowData = (row) => {
        return isSearchPage ? (React.createElement(React.Fragment, null,
            React.createElement("li", { style: { width: CAMPAIGN_TABLE_COLUMNS[0].width } }, getCampaignName(row)),
            React.createElement("li", { style: { width: CAMPAIGN_TABLE_COLUMNS[1].width } }, getText(row.pacingEnabled)),
            React.createElement("li", { style: { width: CAMPAIGN_TABLE_COLUMNS[2].width } }, getText(row.startsOn)),
            React.createElement("li", { style: { width: CAMPAIGN_TABLE_COLUMNS[3].width } }, getText(row.endsOn)),
            React.createElement("li", { style: { width: CAMPAIGN_TABLE_COLUMNS[4].width } }, getText(prefixWithRupee(getParsedMetricValue(formatNumberIntl({ value: row.budget }))))),
            React.createElement("li", { style: { width: CAMPAIGN_TABLE_COLUMNS[5].width } }, getText(prefixWithRupee(getParsedMetricValue(formatNumberIntl({
                value: row.spend,
            }))))),
            React.createElement("li", { style: { width: CAMPAIGN_TABLE_COLUMNS[6].width } }, getText(getParsedMetricValue(formatNumberIntl({
                value: row.roi,
                maximumFractionDigits: 2,
            })))))) : (React.createElement(React.Fragment, null,
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[0].width } }, getCampaignName(row)),
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[1].width } }, getText(row.endsOn)),
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[2].width } }, getText(prefixWithRupee(getParsedMetricValue(formatNumberIntl({ value: row.budget }))))),
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[3].width } }, getText(prefixWithRupee(getParsedMetricValue(formatNumberIntl({
                value: row.spend,
            }))))),
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[4].width } }, getText(getParsedMetricValue(formatNumberIntl({ value: row.impressions })))),
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[5].width } }, getText(getParsedMetricValue(formatNumberIntl({ value: row.cartAdditions })))),
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[6].width } }, getText(prefixWithRupee(getParsedMetricValue(formatNumberIntl({ value: row.gmv }))))),
            React.createElement("li", { style: { width: PERFORMANCE_TABLE_COLUMNS[7].width } }, getText(getParsedMetricValue(formatNumberIntl({
                value: row.roi,
                maximumFractionDigits: 2,
            }))))));
    };
    const renderRow = (row, index) => {
        return (React.createElement("ul", { className: cx(style.tableRow, style.rowList, tableStyles.activeCampaginRow), onClick: () => handleCamapginRowClick(row) }, getRowData(row)));
    };
    const renderPlaceholder = () => {
        return (React.createElement(CampaginsTablePlaceholder, { className: tableStyles.placeholder }));
    };
    return (React.createElement(Table, { classNames: {
            container: tableStyles.table,
            tableBody: tableStyles.tableBody,
        }, columns: isSearchPage
            ? CAMPAIGN_TABLE_COLUMNS
            : PERFORMANCE_TABLE_COLUMNS, data: tableData, renderRow: renderRow, onSort: handleTableSort, renderPlaceholder: renderPlaceholder }));
};
export default CampaignTable;
